import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Search, Play, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { diagnosticsAPI, hostawayAPI, smoobuAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * UK Hostaway Listings -- recognition surface + reservation backfill front
 * door, next to the UK Booking Census card.
 *
 * Recognition (change #1): every live Hostaway listing (kept fresh by the
 * hourly scheduled sync, or the "Refresh from Hostaway" action here, which
 * reuses hostawayAPI.syncListings -- never a second listings fetcher),
 * joined with its hostaway_listing_map row. owned/FA-recognized reuses
 * services.hostaway.is_listing_owned via GET /admin/uk-hostaway-listings;
 * writes go through the existing PATCH .../owned and POST /listing-map
 * endpoints (routes/hostaway.py) -- this panel never re-implements those
 * writers, only composes them with a Preview/Apply confirm step.
 *
 * Backfill (change #2): a manual run-now for a listing set + explicit
 * arrival-date window, since the standard incremental sync only re-fetches
 * reservations Hostaway has MODIFIED since the last run -- an old,
 * never-touched-since booking on a newly-recognized listing would
 * otherwise never resurface. Preview shows counts per listing per month
 * before any write; Apply ingests via the exact same normalize+upsert+
 * dedup path the scheduled sync uses (services/hostaway.py
 * apply_backfill_window), so it is idempotent and there is no parallel
 * writer.
 */
export function UKHostawayListingManagementPanel() {
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const [smoobuProperties, setSmoobuProperties] = useState([]);
  const [mapEdits, setMapEdits] = useState({});
  const [ownedConfirm, setOwnedConfirm] = useState(null);
  const [mapConfirm, setMapConfirm] = useState(null);
  const [savingListingId, setSavingListingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: listingsData }, { data: smoobuData }] = await Promise.all([
        diagnosticsAPI.ukHostawayListings(),
        smoobuAPI.getProperties(),
      ]);
      setListings(listingsData?.listings ?? []);
      setSmoobuProperties(smoobuData?.properties ?? []);
      setMapEdits({});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshFromHostaway = useCallback(async () => {
    setLoading(true);
    try {
      await hostawayAPI.syncListings();
      await load();
      toast.success('Refreshed live listings from Hostaway.');
    } catch (err) {
      toast.error(getErrorMessage(err));
      setLoading(false);
    }
  }, [load]);

  const smoobuPropertyName = (id) => smoobuProperties.find((p) => p.smoobu_id === id)?.name || null;

  const editFor = useCallback((listing) => (
    mapEdits[listing.hostaway_listing_id] ?? {
      smoobu_property_id: listing.map_row?.smoobu_property_id ?? '',
      cutover_date: listing.map_row?.cutover_date ?? '',
    }
  ), [mapEdits]);

  const setEdit = (listing, patch) => {
    setMapEdits((prev) => {
      const current = prev[listing.hostaway_listing_id] ?? {
        smoobu_property_id: listing.map_row?.smoobu_property_id ?? '',
        cutover_date: listing.map_row?.cutover_date ?? '',
      };
      return { ...prev, [listing.hostaway_listing_id]: { ...current, ...patch } };
    });
  };

  const requestOwnedToggle = (listing, nextOwned) => {
    setOwnedConfirm({
      listingId: listing.hostaway_listing_id,
      name: listing.name || listing.hostaway_listing_id,
      before: listing.owned,
      after: nextOwned,
    });
  };

  const applyOwnedToggle = useCallback(async () => {
    if (!ownedConfirm) return;
    const { listingId, after, name } = ownedConfirm;
    setSavingListingId(listingId);
    try {
      const { data } = await hostawayAPI.updatePropertyOwned(listingId, after);
      toast.success(
        after && data?.reservations_restored
          ? `${name} marked owned. ${data.reservations_restored} previously-excluded reservation(s) restored.`
          : `${name} marked ${after ? 'owned' : 'not owned'}.`
      );
      setOwnedConfirm(null);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingListingId(null);
    }
  }, [ownedConfirm, load]);

  const requestMapSave = (listing) => {
    const edit = editFor(listing);
    setMapConfirm({
      listingId: listing.hostaway_listing_id,
      name: listing.name || listing.hostaway_listing_id,
      before: {
        smoobu_property_id: listing.map_row?.smoobu_property_id ?? null,
        cutover_date: listing.map_row?.cutover_date ?? null,
      },
      after: {
        smoobu_property_id: edit.smoobu_property_id || null,
        cutover_date: edit.cutover_date || null,
      },
    });
  };

  const applyMapSave = useCallback(async () => {
    if (!mapConfirm) return;
    const { listingId, after, name } = mapConfirm;
    setSavingListingId(listingId);
    try {
      await hostawayAPI.saveListingMap({
        hostaway_listing_id: listingId,
        property_id: listingId,
        smoobu_property_id: after.smoobu_property_id,
        cutover_date: after.cutover_date,
      });
      toast.success(`Saved mapping for ${name}.`);
      setMapConfirm(null);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingListingId(null);
    }
  }, [mapConfirm, load]);

  // ── Backfill front door ──────────────────────────────────────────
  const [backfillScope, setBackfillScope] = useState('all');
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillApplying, setBackfillApplying] = useState(false);
  const [backfillApplyConfirmOpen, setBackfillApplyConfirmOpen] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const toggleSelectedListing = (id) => {
    setSelectedListingIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setBackfillPreview(null);
    setBackfillResult(null);
  };

  const backfillBody = useMemo(() => ({
    listing_ids: backfillScope === 'all' ? null : selectedListingIds,
    date_from: dateFrom,
    date_to: dateTo || null,
  }), [backfillScope, selectedListingIds, dateFrom, dateTo]);

  const runBackfillPreview = useCallback(async () => {
    if (!dateFrom) {
      toast.error('Set a start date first.');
      return;
    }
    if (backfillScope === 'selected' && selectedListingIds.length === 0) {
      toast.error('Select at least one listing, or switch to All UK.');
      return;
    }
    setBackfillLoading(true);
    try {
      const { data } = await diagnosticsAPI.ukHostawayBackfillPreview(backfillBody);
      setBackfillPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillLoading(false);
    }
  }, [dateFrom, backfillScope, selectedListingIds, backfillBody]);

  const runBackfillApply = useCallback(async () => {
    setBackfillApplyConfirmOpen(false);
    setBackfillApplying(true);
    try {
      const { data } = await diagnosticsAPI.ukHostawayBackfillApply(backfillBody);
      setBackfillResult(data);
      toast.success(`Backfill applied: ${data.created ?? 0} created, ${data.updated ?? 0} updated.`);
      await runBackfillPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillApplying(false);
    }
  }, [backfillBody, runBackfillPreview]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-amber-600" />
            UK Hostaway Listings
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Every live Hostaway listing, whether FA recognizes it (ingests its reservations), and
            its Smoobu mapping for migrated properties. Set owned, or map/edit a listing&apos;s
            Smoobu counterpart and cutover date -- every write shows a Preview before it commits.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={refreshFromHostaway} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh from Hostaway
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 font-medium">Listing</th>
                    <th className="text-left px-3 py-2 font-medium">Owned</th>
                    <th className="text-left px-3 py-2 font-medium">FA recognized</th>
                    <th className="text-left px-3 py-2 font-medium">Mapped Smoobu property</th>
                    <th className="text-left px-3 py-2 font-medium">Cutover date</th>
                    <th className="text-left px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((l) => {
                    const edit = editFor(l);
                    const dirty =
                      (edit.smoobu_property_id || '') !== (l.map_row?.smoobu_property_id ?? '') ||
                      (edit.cutover_date || '') !== (l.map_row?.cutover_date ?? '');
                    return (
                      <tr key={l.hostaway_listing_id} className={`border-b ${!l.fa_recognized ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                        <td className="px-3 py-2">
                          <div>{l.name || l.hostaway_listing_id}</div>
                          <div className="text-xs text-muted-foreground font-mono">{l.hostaway_listing_id}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Switch
                            data-testid={`uk-listing-owned-switch-${l.hostaway_listing_id}`}
                            checked={!!l.owned}
                            disabled={savingListingId === l.hostaway_listing_id}
                            onCheckedChange={(checked) => requestOwnedToggle(l, checked)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            data-testid={`uk-listing-fa-recognized-${l.hostaway_listing_id}`}
                            variant={l.fa_recognized ? 'outline' : 'destructive'}
                            className="text-xs"
                          >
                            {l.fa_recognized ? 'Recognized' : 'Not recognized'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            data-testid={`uk-listing-map-select-${l.hostaway_listing_id}`}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-xs"
                            value={edit.smoobu_property_id}
                            onChange={(e) => setEdit(l, { smoobu_property_id: e.target.value })}
                          >
                            <option value="">(no Smoobu history)</option>
                            {edit.smoobu_property_id && !smoobuPropertyName(edit.smoobu_property_id) && (
                              <option value={edit.smoobu_property_id}>
                                {edit.smoobu_property_id} (not in synced list)
                              </option>
                            )}
                            {smoobuProperties.map((p) => (
                              <option key={p.smoobu_id} value={p.smoobu_id}>
                                {p.name || p.smoobu_id}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="date"
                            data-testid={`uk-listing-cutover-date-${l.hostaway_listing_id}`}
                            className="h-9 text-xs w-36"
                            value={edit.cutover_date || ''}
                            onChange={(e) => setEdit(l, { cutover_date: e.target.value })}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!dirty || savingListingId === l.hostaway_listing_id}
                            onClick={() => requestMapSave(l)}
                            data-testid={`uk-listing-map-save-${l.hostaway_listing_id}`}
                          >
                            {savingListingId === l.hostaway_listing_id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : 'Save'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {listings.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">
                        No synced Hostaway listings yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">UK Hostaway Reservation Backfill</CardTitle>
          <p className="text-sm text-muted-foreground">
            Run-now for a listing set + explicit arrival-date window. Needed because the hourly
            sync only re-fetches reservations Hostaway shows as modified since its last run; an
            old, untouched booking on a newly-recognized listing needs this to surface. Preview
            shows counts per listing per month before Apply ingests via the same normalization
            and dedup path as the scheduled sync -- idempotent on the Hostaway reservation id.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Scope</label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={backfillScope === 'all' ? 'default' : 'outline'}
                  onClick={() => { setBackfillScope('all'); setBackfillPreview(null); setBackfillResult(null); }}
                  data-testid="uk-backfill-scope-all"
                >
                  All UK
                </Button>
                <Button
                  size="sm"
                  variant={backfillScope === 'selected' ? 'default' : 'outline'}
                  onClick={() => { setBackfillScope('selected'); setBackfillPreview(null); setBackfillResult(null); }}
                  data-testid="uk-backfill-scope-selected"
                >
                  Selected listings
                </Button>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">From (arrival date)</label>
              <Input
                type="date"
                className="h-9 text-sm"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setBackfillPreview(null); }}
                data-testid="uk-backfill-date-from"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">To (exclusive, optional)</label>
              <Input
                type="date"
                className="h-9 text-sm"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setBackfillPreview(null); }}
                data-testid="uk-backfill-date-to"
              />
            </div>
            <Button onClick={runBackfillPreview} disabled={backfillLoading} size="sm" data-testid="uk-backfill-preview-btn">
              {backfillLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-1.5 h-3.5 w-3.5" />}
              Preview
            </Button>
            <Button
              onClick={() => setBackfillApplyConfirmOpen(true)}
              disabled={!backfillPreview || backfillLoading || backfillApplying}
              size="sm"
              data-testid="uk-backfill-apply-btn"
            >
              {backfillApplying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
              Apply
            </Button>
          </div>

          {backfillScope === 'selected' && (
            <div className="flex flex-wrap gap-2">
              {listings.map((l) => (
                <label key={l.hostaway_listing_id} className="flex items-center gap-1.5 text-xs border rounded px-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedListingIds.includes(l.hostaway_listing_id)}
                    onChange={() => toggleSelectedListing(l.hostaway_listing_id)}
                    data-testid={`uk-backfill-listing-checkbox-${l.hostaway_listing_id}`}
                  />
                  {l.name || l.hostaway_listing_id}
                </label>
              ))}
            </div>
          )}

          {backfillPreview && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm bg-muted/30 rounded p-3">
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Total in window:</span>
                  <span data-testid="uk-backfill-total-in-window">{backfillPreview.total_in_window}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-muted-foreground">New:</span>
                  <span data-testid="uk-backfill-total-new">{backfillPreview.totals?.new}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Updated:</span>
                  <span>{backfillPreview.totals?.updated}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Unowned (skipped):</span>
                  <span>{backfillPreview.totals?.unowned}</span>
                </div>
              </div>
              {backfillPreview.rows?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-2">Listing</th>
                        <th className="py-1 pr-2">Month</th>
                        <th className="py-1 pr-2">New</th>
                        <th className="py-1 pr-2">Updated</th>
                        <th className="py-1 pr-2">Skipped</th>
                        <th className="py-1 pr-2">Unmapped</th>
                        <th className="py-1 pr-2">Unowned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backfillPreview.rows.map((row, i) => (
                        <tr key={i} className="border-b last:border-b-0" data-testid={`uk-backfill-row-${row.listing_id}-${row.month}`}>
                          <td className="py-1 pr-2">{row.listing_name || row.listing_id}</td>
                          <td className="py-1 pr-2 font-mono">{row.month}</td>
                          <td className="py-1 pr-2">{row.new}</td>
                          <td className="py-1 pr-2">{row.updated}</td>
                          <td className="py-1 pr-2">{row.skipped}</td>
                          <td className="py-1 pr-2">{row.unmapped}</td>
                          <td className="py-1 pr-2">{row.unowned}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {backfillResult && (
            <div className="text-xs text-muted-foreground bg-muted/20 rounded p-3" data-testid="uk-backfill-result">
              Applied: {backfillResult.created ?? 0} created, {backfillResult.updated ?? 0} updated,
              {' '}{backfillResult.unowned ?? 0} unowned skipped. Re-run Preview above to confirm the
              window is now a no-op (idempotency).
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!ownedConfirm} onOpenChange={(open) => !open && setOwnedConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm ownership change</AlertDialogTitle>
            <AlertDialogDescription>
              {ownedConfirm && (
                <>
                  {ownedConfirm.name}: owned {String(ownedConfirm.before)} &rarr; {String(ownedConfirm.after)}.
                  {ownedConfirm.after
                    ? ' Future syncs will ingest this listing’s reservations. Historical reservations still need a Backfill run below.'
                    : ' Future syncs will skip this listing. Already-ingested reservations are not removed automatically.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="uk-listing-owned-confirm-apply" onClick={applyOwnedToggle}>
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!mapConfirm} onOpenChange={(open) => !open && setMapConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Smoobu mapping change</AlertDialogTitle>
            <AlertDialogDescription>
              {mapConfirm && (
                <>
                  {mapConfirm.name}:<br />
                  Before: {smoobuPropertyName(mapConfirm.before.smoobu_property_id) || mapConfirm.before.smoobu_property_id || '(unmapped)'}
                  {' '}(cutover {mapConfirm.before.cutover_date || 'n/a'})<br />
                  After: {smoobuPropertyName(mapConfirm.after.smoobu_property_id) || mapConfirm.after.smoobu_property_id || '(unmapped)'}
                  {' '}(cutover {mapConfirm.after.cutover_date || 'n/a'})
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="uk-listing-map-confirm-apply" onClick={applyMapSave}>
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={backfillApplyConfirmOpen} onOpenChange={setBackfillApplyConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm reservation backfill</AlertDialogTitle>
            <AlertDialogDescription>
              Ingests {backfillPreview?.total_in_window ?? 0} reservation(s)
              ({backfillPreview?.totals?.new ?? 0} new, {backfillPreview?.totals?.updated ?? 0} updated)
              for {backfillScope === 'all' ? 'all UK listings' : `${selectedListingIds.length} selected listing(s)`},
              arrivals {dateFrom} through {dateTo || 'open-ended'}. Idempotent -- safe to re-run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="uk-backfill-confirm-apply" onClick={runBackfillApply}>
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
